import React, { useState } from 'react'
import { LogIn, UserPlus, Mail, Lock, User } from 'lucide-react'

const Login = ({ onSignIn, onSignUp, onConfirmSignUp, onResendCode, isLoading, error }) => {
  const [mode, setMode] = useState('signin') // 'signin' or 'signup' or 'confirm'
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [pendingUsername, setPendingUsername] = useState('')
  const [localError, setLocalError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const clearErrors = () => {
    setLocalError('')
    if (error) {
      // Clear error from parent if there's a clearError function
    }
  }

  // Password validation function
  const validatePassword = (pwd) => {
    const requirements = {
      minLength: pwd.length >= 8,
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
    }
    
    return {
      isValid: Object.values(requirements).every(req => req === true),
      requirements,
    }
  }

  const handleSignIn = async (e) => {
    e.preventDefault()
    clearErrors()
    
    if (!username || !password) {
      setLocalError('Please enter both username and password')
      return
    }

    const result = await onSignIn(username, password)
    if (!result.success) {
      setLocalError(result.error || 'Sign in failed')
    }
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    clearErrors()
    
    if (!username || !email || !password || !confirmPassword || !firstName || !lastName) {
      setLocalError('Please fill in all fields')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setLocalError('Please enter a valid email address')
      return
    }

    // Password validation
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      const missingRequirements = []
      if (!passwordValidation.requirements.minLength) missingRequirements.push('at least 8 characters')
      if (!passwordValidation.requirements.hasNumber) missingRequirements.push('1 number')
      if (!passwordValidation.requirements.hasSpecialChar) missingRequirements.push('1 special character')
      if (!passwordValidation.requirements.hasUppercase) missingRequirements.push('1 uppercase letter')
      if (!passwordValidation.requirements.hasLowercase) missingRequirements.push('1 lowercase letter')
      
      setLocalError(`Password must contain: ${missingRequirements.join(', ')}`)
      return
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match')
      return
    }

    const result = await onSignUp(username, password, email, firstName, lastName)
    if (result.success && result.requiresConfirmation) {
      setPendingUsername(username)
      setMode('confirm')
      setLocalError('')
    } else if (!result.success) {
      setLocalError(result.error || 'Sign up failed')
    }
  }

  const handleConfirmSignUp = async (e) => {
    e.preventDefault()
    clearErrors()
    
    if (!confirmationCode) {
      setLocalError('Please enter the verification code')
      return
    }

    const result = await onConfirmSignUp(pendingUsername, confirmationCode)
    
    if (result.success && result.isSignUpComplete) {
      // After successful confirmation, switch back to sign-in mode
      setMode('signin')
      setConfirmationCode('')
      setPendingUsername('')
      setLocalError('')
      
      // Show success message after switching to sign-in mode
      setTimeout(() => {
        alert('Account verified! Please sign in with your credentials.')
      }, 100)
    } else if (!result.success) {
      setLocalError(result.error || 'Verification failed')
    }
  }

  const handleResendCode = async () => {
    clearErrors()
    const result = await onResendCode(pendingUsername)
    if (result.success) {
      setLocalError('')
      alert('Verification code has been resent to your email')
    } else {
      setLocalError(result.error || 'Failed to resend code')
    }
  }

  const displayError = localError || error

  // OAuth sign-in handlers (stubbed for future implementation)
  const handleGoogleSignIn = () => {
    // TODO: Implement Google OAuth when available
    alert('Coming soon!')
  }

  const handleFacebookSignIn = () => {
    // TODO: Implement Facebook OAuth when available
    alert('Coming soon!')
  }

  const handleAppleSignIn = () => {
    // TODO: Implement Apple OAuth when available
    alert('Coming soon!')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col items-center justify-center p-4">
      <div className="relative">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/sportigee-blue-no-background.png" 
              alt="Sportigee Logo" 
              className="h-24 w-auto"
            />
          </div>
          <p className="text-gray-600">
            {mode === 'signin' && 'Sign in to access the application'}
            {mode === 'signup' && 'Create a new account'}
            {mode === 'confirm' && 'Verify your email address'}
          </p>
        </div>

        {/* Mode Tabs */}
        {mode !== 'confirm' && (
          <div className="flex mb-6 border-b border-gray-200">
            <button
              onClick={() => {
                setMode('signin')
                clearErrors()
                setUsername('')
                setPassword('')
                setConfirmPassword('')
                setEmail('')
                setFirstName('')
                setLastName('')
              }}
              className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                mode === 'signin'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                setMode('signup')
                clearErrors()
                setUsername('')
                setPassword('')
                setConfirmPassword('')
                setEmail('')
                setFirstName('')
                setLastName('')
              }}
              className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                mode === 'signup'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Sign In Form */}
        {mode === 'signin' && (
          <>
            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <label htmlFor="signin-username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username or Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="signin-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter your username or email"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {displayError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {displayError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={18} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            {/* OAuth Sign-In Options (Stubbed) */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                <button
                  onClick={handleGoogleSignIn}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Google sign-in (coming soon)"
                >
                  <span className="text-sm font-medium text-gray-700">Google</span>
                </button>
                <button
                  onClick={handleFacebookSignIn}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Facebook sign-in (coming soon)"
                >
                  <span className="text-sm font-medium text-gray-700">Facebook</span>
                </button>
                <button
                  onClick={handleAppleSignIn}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Apple sign-in (coming soon)"
                >
                  <span className="text-sm font-medium text-gray-700">Apple</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Sign Up Form */}
        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="space-y-6">
            <div>
              <label htmlFor="signup-username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="signup-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Choose a username"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="signup-firstname" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  id="signup-firstname"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="First name"
                  required
                />
              </div>
              <div>
                <label htmlFor="signup-lastname" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  id="signup-lastname"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Last name"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    password && !validatePassword(password).isValid
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="signup-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    confirmPassword && password !== confirmPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-600">
                  Passwords do not match
                </p>
              )}
              {confirmPassword && password === confirmPassword && password.length > 0 && (
                <p className="mt-1 text-xs text-green-600">
                  ✓ Passwords match
                </p>
              )}
            </div>

            {displayError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {displayError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  <span>Create Account</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Email Confirmation Form */}
        {mode === 'confirm' && (
          <form onSubmit={handleConfirmSignUp} className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm mb-4">
              <p className="font-medium mb-1">Verification code sent!</p>
              <p>Please check your email ({email || pendingUsername}) for the verification code.</p>
            </div>

            <div>
              <label htmlFor="confirmation-code" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                id="confirmation-code"
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter verification code"
                required
                autoFocus
              />
            </div>

            {displayError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {displayError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying...</span>
                </>
              ) : (
                <span>Verify Email</span>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-sm text-green-600 hover:text-green-700 disabled:opacity-50"
              >
                Resend verification code
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setMode('signin')
                  clearErrors()
                  setConfirmationCode('')
                  setPendingUsername('')
                }}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
        </div>
        
        {/* Password Requirements - Outside Modal */}
        {mode === 'signup' && password && (
          <>
            {/* Desktop: Side of modal */}
            <div className="absolute left-full ml-4 w-64 hidden lg:block" style={{ top: '1rem' }}>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                <ul className="space-y-1 text-xs">
                  <li className={`flex items-center ${password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{password.length >= 8 ? '✓' : '○'}</span>
                    At least 8 characters
                  </li>
                  <li className={`flex items-center ${/\d/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{/\d/.test(password) ? '✓' : '○'}</span>
                    At least 1 number
                  </li>
                  <li className={`flex items-center ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? '✓' : '○'}</span>
                    At least 1 special character
                  </li>
                  <li className={`flex items-center ${/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{/[A-Z]/.test(password) ? '✓' : '○'}</span>
                    At least 1 uppercase letter
                  </li>
                  <li className={`flex items-center ${/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{/[a-z]/.test(password) ? '✓' : '○'}</span>
                    At least 1 lowercase letter
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Mobile: Below modal */}
      {mode === 'signup' && password && (
        <div className="mt-4 w-full max-w-md lg:hidden">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
            <ul className="space-y-1 text-xs">
              <li className={`flex items-center ${password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                <span className="mr-2">{password.length >= 8 ? '✓' : '○'}</span>
                At least 8 characters
              </li>
              <li className={`flex items-center ${/\d/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                <span className="mr-2">{/\d/.test(password) ? '✓' : '○'}</span>
                At least 1 number
              </li>
              <li className={`flex items-center ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                <span className="mr-2">{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? '✓' : '○'}</span>
                At least 1 special character
              </li>
              <li className={`flex items-center ${/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                <span className="mr-2">{/[A-Z]/.test(password) ? '✓' : '○'}</span>
                At least 1 uppercase letter
              </li>
              <li className={`flex items-center ${/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                <span className="mr-2">{/[a-z]/.test(password) ? '✓' : '○'}</span>
                At least 1 lowercase letter
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default Login
